import React from 'react';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faTableList } from '@fortawesome/free-solid-svg-icons';

import { Greet, Attribute } from '~/types/models';
import Section from '~/App/Modules/TodoList/Components/GreetDetails/Section';
import ContactList from '~/App/Components/ContactList';
import ContactListItem from '~/App/Components/ContactListItem';
import { useQueryClient } from 'react-query';
import { first } from 'lodash';
import { useAttributes } from '~/App/Api/Attributes';
import AttributeList from '../../../Modules/TodoList/Components/GreetDetails/AttributeList';
import UserHistory from '../../../Modules/TodoList/Components/GreetDetails/UserHistory';
import { openWhatAreCustomFieldsModal } from '~/App/Components/Modals/WhatAreCustomFieldsModal';

export interface GetAllAttributesResponse {
  message: string;
  data: Attribute[];
  profile_was_updated: boolean;
  automation_id?: string;
}

export interface CustomerContextProps {
  greet: Greet;
  className?: string;
}

type ContainerProps = {
  children: React.ReactNode;
  className?: string;
};

function Container({ children, className }: ContainerProps) {
  return (
    <div
      className={cx(
        'border border-solid v2-border-neutral-300 w-full rounded p-[30px] overflow-y-auto',
        className
      )}
    >
      {children}
    </div>
  );
}

export default function CustomerContext({
  greet,
  className,
}: CustomerContextProps) {
  const { profiles } = greet;
  const profile = first(profiles);

  const queryClient = useQueryClient();
  const { status, data: attributes } = useAttributes({
    greetId: greet.id,
    profileId: profile?.id,
    options: {
      onSuccess: (attributesData) => {
        if (attributesData.profile_was_updated) {
          queryClient.invalidateQueries('todos');
          queryClient.invalidateQueries(['greet', { id: greet.id }]);
        }
      },
    },
  });

  const customFields =
    attributes?.data.filter((attribute) => attribute.name !== 'Reputation') ??
    [];

  if (profiles.length > 1) {
    return (
      <Section headerText='Group Members'>
        <ContactList>
          {profiles.map((profile) => (
            <ContactListItem key={profile.id} profile={profile} />
          ))}
        </ContactList>
      </Section>
    );
  }

  return (
    <Container className={className}>
      <div className='flex flex-wrap items-center justify-between mb-4'>
        <div className='flex items-center gap-x-2.5 text-[21px] font-bold text-black'>
          <FontAwesomeIcon icon={faTableList} />
          <span>Imported Customer Info</span>
        </div>
        <div
          onClick={openWhatAreCustomFieldsModal}
          className='text-base text-neutral-500 underline underline-offset-4 hover:text-neutral-900 ml-1'
          role='button'
        >
          What is this?
        </div>
      </div>
      <AttributeList status={status} attributes={customFields} />
      <div className='flex gap-x-2.5 items-center text-[21px] font-bold text-black mb-4 mt-7'>
        <FontAwesomeIcon icon={faClock} />
        <span>User history</span>
      </div>
      {profile && <UserHistory profile={profile} />}
    </Container>
  );
}
